import type { Table, VisibilityState } from '@tanstack/react-table';
import { Columns3, History } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui-kit/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui-kit/dropdown-menu';

/** Screens this wide show every column (prototype `colsDefault`); narrower ones start without the optional ones. */
const WIDE_SCREEN = 1440;

/** The visibility a table starts with, or returns to on "Reset to default". */
export const defaultColumnVisibilityOf = (defaults: VisibilityState = {}): VisibilityState =>
  typeof window !== 'undefined' && window.innerWidth >= WIDE_SCREEN ? {} : defaults;

const storageKeyOf = (key: string) => `hr-columns:${key}`;

/** The choice remembered in this browser for the table, else the default. */
export const initialColumnVisibility = (
  defaults: VisibilityState | undefined,
  key?: string
): VisibilityState => {
  if (key) {
    try {
      const stored = localStorage.getItem(storageKeyOf(key));
      if (stored) return JSON.parse(stored) as VisibilityState;
    } catch {
      // Storage blocked or unreadable: fall back to the default.
    }
  }
  return defaultColumnVisibilityOf(defaults);
};

export const storeColumnVisibility = (key: string, visibility: VisibilityState) => {
  try {
    localStorage.setItem(storageKeyOf(key), JSON.stringify(visibility));
  } catch {
    // Storage blocked: the choice lasts until reload.
  }
};

interface ColumnsMenuProps<TData> {
  table: Table<TData>;
  /** The optional columns the menu offers (the others always show). */
  columns: { id: string; label: string }[];
  /** What "Reset to default" restores (`defaultColumnVisibility` of the table). */
  defaults?: VisibilityState;
  className?: string;
}

/**
 * The Columns menu (prototype `colsBtn`): "Columns n/N", a checklist of the optional columns that stays
 * open while toggling, and "Reset to default".
 */
export function ColumnsMenu<TData>({ table, columns, defaults, className }: ColumnsMenuProps<TData>) {
  const { t } = useTranslation();
  const shown = columns.filter((column) => table.getColumn(column.id)?.getIsVisible()).length;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="outline" className={className}>
          <Columns3 />
          {t('COLUMNS')}
          <span className="text-xs font-normal text-muted-foreground">
            {shown}/{columns.length}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-48">
        <DropdownMenuLabel>{t('SHOW_COLUMNS')}</DropdownMenuLabel>
        {columns.map((column) => (
          <DropdownMenuCheckboxItem
            key={column.id}
            checked={table.getColumn(column.id)?.getIsVisible() ?? false}
            onCheckedChange={(visible) => table.getColumn(column.id)?.toggleVisibility(visible)}
            // The menu stays open while columns are toggled.
            onSelect={(event) => event.preventDefault()}
          >
            {column.label}
          </DropdownMenuCheckboxItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={(event) => {
            event.preventDefault();
            table.setColumnVisibility(defaultColumnVisibilityOf(defaults));
          }}
        >
          <History />
          {t('RESET_TO_DEFAULT')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
